import { furyTracks, musicCues, musicPlans, soundEffects, type MusicCue, type MusicScene, type MusicTrack, type SoundEffect } from './catalog';
import { ShuffleBag } from './shuffleBag';

const CROSSFADE_MS = 1200;
const FURY_FADE_MS = 420;

export class MusicEngine {
  private readonly players = [new Audio(), new Audio()];
  private readonly furyPlayer = new Audio();
  private readonly effectPlayers = Array.from({ length: 5 }, () => new Audio());
  private readonly gains = [0, 0];
  private readonly bags = new Map<string, ShuffleBag<MusicTrack>>();
  private currentPlayer = -1;
  private currentTrack?: MusicTrack;
  private activeScene?: MusicScene;
  private stageIndex = 0;
  private cueReturn?: { scene: MusicScene; playerIndex: number; track?: MusicTrack };
  private currentFuryTrack?: MusicTrack;
  private furyActive = false;
  private furyGain = 0;
  private effectCursor = 0;
  private advanceTimer?: number;
  private fadeFrame?: number;
  private enabled = true;
  private volume = 0.65;
  private effectsEnabled = true;
  private effectsVolume = 0.8;

  constructor() {
    this.players.forEach((player) => {
      player.preload = 'auto';
      player.addEventListener('loadedmetadata', () => {
        if (player === this.currentAudio && !player.paused) this.scheduleAdvance();
      });
      player.addEventListener('playing', () => {
        if (player === this.currentAudio) this.scheduleAdvance();
      });
      player.addEventListener('ended', () => {
        if (player !== this.currentAudio) return;
        // A recruitment cue is an interruption, not part of the playlist. Once
        // its single pass ends, retain the paused cue state until the reveal is
        // acknowledged or its page is left; do not loop it or resume early.
        if (this.cueReturn) {
          this.clearTiming();
          return;
        }
        this.advance(0);
      });
      player.addEventListener('error', () => {
        if (player === this.currentAudio) this.advance(0);
      });
    });
    this.furyPlayer.preload = 'auto';
    this.furyPlayer.addEventListener('ended', () => {
      if (this.furyActive) this.playNextFuryTrack();
    });
    this.furyPlayer.addEventListener('error', () => {
      if (this.furyActive) this.playNextFuryTrack();
    });
    this.effectPlayers.forEach((player) => { player.preload = 'auto'; });
  }

  setScene(scene: MusicScene) {
    if (this.furyActive && scene !== 'match') this.stopFury(false);
    if (this.activeScene === scene && !this.cueReturn) return;
    this.clearCue();
    this.activeScene = scene;
    this.stageIndex = 0;
    this.playTrack(this.nextForCurrentStage(), CROSSFADE_MS);
  }

  playCue(cue: MusicCue) {
    if (!this.activeScene || this.cueReturn) return;
    if (this.furyActive) this.stopFury(false);
    this.clearTiming();
    const outgoingIndex = this.currentPlayer;
    const incomingIndex = outgoingIndex === 0 ? 1 : 0;
    const incoming = this.players[incomingIndex];
    this.cueReturn = { scene: this.activeScene, playerIndex: outgoingIndex, track: this.currentTrack };

    if (outgoingIndex >= 0) {
      this.players[outgoingIndex].pause();
      this.gains[outgoingIndex] = 0;
    }
    incoming.pause();
    incoming.loop = false;
    incoming.src = musicCues[cue].src;
    incoming.currentTime = 0;
    this.currentPlayer = incomingIndex;
    this.currentTrack = musicCues[cue];
    this.gains[incomingIndex] = 1;
    this.applyVolumes();
    if (this.enabled) void incoming.play().catch(() => undefined);
  }

  finishCue() {
    const returning = this.cueReturn;
    if (!returning) return;
    this.clearTiming();

    const cueIndex = this.currentPlayer;
    if (cueIndex >= 0) {
      const cuePlayer = this.players[cueIndex];
      cuePlayer.loop = false;
      cuePlayer.pause();
      cuePlayer.currentTime = 0;
      this.gains[cueIndex] = 0;
    }

    this.cueReturn = undefined;
    this.activeScene = returning.scene;
    if (returning.playerIndex >= 0) {
      this.currentPlayer = returning.playerIndex;
      this.currentTrack = returning.track;
      this.gains[returning.playerIndex] = 1;
      this.applyVolumes();
      if (this.enabled) void this.currentAudio?.play().then(() => this.scheduleAdvance()).catch(() => undefined);
      return;
    }

    this.currentPlayer = -1;
    this.currentTrack = undefined;
    this.playTrack(this.nextForCurrentStage(), 0);
  }

  setFuryActive(active: boolean) {
    if (active === this.furyActive) return;
    if (active) {
      if (this.activeScene === 'match') this.startFury();
    } else {
      this.stopFury(true);
    }
  }

  playEffect(effect: SoundEffect) {
    if (!this.effectsEnabled) return;
    const player = this.effectPlayers.find((candidate) => candidate.paused || candidate.ended)
      ?? this.effectPlayers[this.effectCursor++ % this.effectPlayers.length];
    player.pause();
    player.src = soundEffects[effect].src;
    player.currentTime = 0;
    player.volume = this.effectsVolume;
    void player.play().catch(() => undefined);
  }

  setEnabled(enabled: boolean) {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (enabled) {
      this.applyVolumes();
      if (this.furyActive) void this.furyPlayer.play().catch(() => undefined);
      else void this.currentAudio?.play().then(() => this.scheduleAdvance()).catch(() => undefined);
    } else {
      window.clearTimeout(this.advanceTimer);
      this.players.forEach((player) => player.pause());
      this.furyPlayer.pause();
    }
  }

  setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
    this.applyVolumes();
  }

  setEffectsEnabled(enabled: boolean) {
    this.effectsEnabled = enabled;
    if (!enabled) this.effectPlayers.forEach((player) => {
      player.pause();
      player.currentTime = 0;
    });
  }

  setEffectsVolume(volume: number) {
    this.effectsVolume = Math.max(0, Math.min(1, volume));
    this.effectPlayers.forEach((player) => { player.volume = this.effectsVolume; });
  }

  unlock() {
    if (!this.enabled) return;
    if (this.furyActive) {
      if (this.furyPlayer.paused) void this.furyPlayer.play().catch(() => undefined);
      return;
    }
    if (this.currentAudio?.paused) void this.currentAudio.play().then(() => this.scheduleAdvance()).catch(() => undefined);
  }

  destroy() {
    this.clearTiming();
    this.players.forEach((player) => {
      player.pause();
      player.removeAttribute('src');
      player.load();
    });
    this.furyPlayer.pause();
    this.furyPlayer.removeAttribute('src');
    this.furyPlayer.load();
    this.effectPlayers.forEach((player) => {
      player.pause();
      player.removeAttribute('src');
      player.load();
    });
    this.currentPlayer = -1;
    this.currentTrack = undefined;
    this.activeScene = undefined;
    this.cueReturn = undefined;
    this.currentFuryTrack = undefined;
    this.furyActive = false;
    this.furyGain = 0;
    this.gains[0] = 0;
    this.gains[1] = 0;
  }

  private get currentAudio() {
    return this.currentPlayer < 0 ? undefined : this.players[this.currentPlayer];
  }

  private bag(poolId: string, tracks: readonly MusicTrack[]) {
    let bag = this.bags.get(poolId);
    if (!bag) {
      bag = new ShuffleBag(tracks);
      this.bags.set(poolId, bag);
    }
    return bag;
  }

  private nextForCurrentStage() {
    if (!this.activeScene) throw new Error('Cannot select music without an active scene.');
    const stage = musicPlans[this.activeScene].stages[this.stageIndex];
    return this.bag(stage.poolId, stage.tracks).next(this.currentTrack);
  }

  private hasNext() {
    if (this.cueReturn) return false;
    if (!this.activeScene) return false;
    const plan = musicPlans[this.activeScene];
    return plan.stages[this.stageIndex].repeat || this.stageIndex < plan.stages.length - 1;
  }

  private advance(fadeMs = CROSSFADE_MS) {
    window.clearTimeout(this.advanceTimer);
    if (this.cueReturn) { this.finishCue(); return; }
    if (!this.activeScene) return;
    const plan = musicPlans[this.activeScene];
    const stage = plan.stages[this.stageIndex];
    if (!stage.repeat && this.stageIndex >= plan.stages.length - 1) return;
    if (!stage.repeat) this.stageIndex += 1;
    this.playTrack(this.nextForCurrentStage(), fadeMs);
  }

  private startFury() {
    this.clearTiming();
    this.furyActive = true;
    this.players.forEach((player, index) => {
      if (index !== this.currentPlayer) this.stopPlayer(index);
    });
    if (this.currentPlayer >= 0) this.gains[this.currentPlayer] = 1;
    this.playNextFuryTrack();

    if (!this.enabled) {
      this.currentAudio?.pause();
      return;
    }
    const matchPlayer = this.currentAudio;
    const startedAt = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / FURY_FADE_MS);
      if (this.currentPlayer >= 0) this.gains[this.currentPlayer] = 1 - progress;
      this.furyGain = progress;
      this.applyVolumes();
      if (progress < 1) this.fadeFrame = window.requestAnimationFrame(tick);
      else {
        matchPlayer?.pause();
        if (this.currentPlayer >= 0) this.gains[this.currentPlayer] = 1;
        this.applyVolumes();
      }
    };
    this.fadeFrame = window.requestAnimationFrame(tick);
  }

  private stopFury(resumeMatch: boolean) {
    if (!this.furyActive) return;
    this.clearTiming();
    this.furyActive = false;
    const matchPlayer = resumeMatch && this.activeScene === 'match' ? this.currentAudio : undefined;
    if (!matchPlayer || !this.enabled) {
      this.stopFuryPlayer();
      if (this.currentPlayer >= 0) this.gains[this.currentPlayer] = 1;
      this.applyVolumes();
      return;
    }

    this.gains[this.currentPlayer] = 0;
    void matchPlayer.play().catch(() => undefined);
    const startedAt = performance.now();
    const furyStart = this.furyGain;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / FURY_FADE_MS);
      this.furyGain = furyStart * (1 - progress);
      this.gains[this.currentPlayer] = progress;
      this.applyVolumes();
      if (progress < 1) this.fadeFrame = window.requestAnimationFrame(tick);
      else {
        this.stopFuryPlayer();
        this.gains[this.currentPlayer] = 1;
        this.applyVolumes();
        this.scheduleAdvance();
      }
    };
    this.fadeFrame = window.requestAnimationFrame(tick);
  }

  private playNextFuryTrack() {
    this.currentFuryTrack = this.bag('fury', furyTracks).next(this.currentFuryTrack);
    this.furyPlayer.pause();
    this.furyPlayer.src = this.currentFuryTrack.src;
    this.furyPlayer.currentTime = 0;
    this.applyVolumes();
    if (this.enabled) void this.furyPlayer.play().catch(() => undefined);
  }

  private stopFuryPlayer() {
    this.furyPlayer.pause();
    this.furyPlayer.currentTime = 0;
    this.furyGain = 0;
  }

  private playTrack(track: MusicTrack, fadeMs: number) {
    this.clearTiming();
    const outgoingIndex = this.currentPlayer;
    const incomingIndex = outgoingIndex === 0 ? 1 : 0;
    const incoming = this.players[incomingIndex];
    incoming.pause();
    incoming.loop = false;
    incoming.src = track.src;
    incoming.currentTime = 0;
    this.gains[incomingIndex] = outgoingIndex < 0 ? 1 : 0;
    this.currentPlayer = incomingIndex;
    this.currentTrack = track;
    this.applyVolumes();

    if (this.enabled) void incoming.play().then(() => this.scheduleAdvance()).catch(() => undefined);
    if (outgoingIndex < 0 || fadeMs <= 0) {
      if (outgoingIndex >= 0) this.stopPlayer(outgoingIndex);
      this.gains[incomingIndex] = 1;
      this.applyVolumes();
      return;
    }

    const startedAt = performance.now();
    const outgoingStart = this.gains[outgoingIndex];
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / fadeMs);
      this.gains[outgoingIndex] = outgoingStart * (1 - progress);
      this.gains[incomingIndex] = progress;
      this.applyVolumes();
      if (progress < 1) this.fadeFrame = window.requestAnimationFrame(tick);
      else this.stopPlayer(outgoingIndex);
    };
    this.fadeFrame = window.requestAnimationFrame(tick);
  }

  private scheduleAdvance() {
    window.clearTimeout(this.advanceTimer);
    const player = this.currentAudio;
    if (!player || player.paused || !this.hasNext() || !Number.isFinite(player.duration)) return;
    const remainingMs = Math.max(0, (player.duration - player.currentTime) * 1000 - CROSSFADE_MS);
    this.advanceTimer = window.setTimeout(() => this.advance(), remainingMs);
  }

  private applyVolumes() {
    this.players.forEach((player, index) => {
      player.volume = this.enabled ? Math.max(0, Math.min(1, this.gains[index] * this.volume)) : 0;
    });
    this.furyPlayer.volume = this.enabled ? Math.max(0, Math.min(1, this.furyGain * this.volume)) : 0;
  }

  private stopPlayer(index: number) {
    this.players[index].loop = false;
    this.players[index].pause();
    this.players[index].currentTime = 0;
    this.gains[index] = 0;
    this.applyVolumes();
  }

  private clearTiming() {
    window.clearTimeout(this.advanceTimer);
    if (this.fadeFrame !== undefined) window.cancelAnimationFrame(this.fadeFrame);
    this.advanceTimer = undefined;
    this.fadeFrame = undefined;
  }

  private clearCue() {
    this.cueReturn = undefined;
    this.players.forEach((player) => { player.loop = false; });
  }
}
